import { useEffect, useState } from "react"
import { api } from "../../env"
import LoadingButton from "../../components/LoadingButton"
import ExpandingTextArea from "../../components/ExpandingTextArea"
import StarRating from "../../components/StarRating"

const MAX_COMMENT = 1000 // same limit as the backend DTO

// The mentee's rating for one completed spark. Lives inside the clickable SparkCard,
// so clicks stop here instead of collapsing the card.
export default function SparkRating({ sparkInstanceId }) {
    const [state, setState] = useState("loading")
    const [saved, setSaved] = useState(null)
    const [saveError, setSaveError] = useState(null)
    const [editing, setEditing] = useState(false)
    const [rating, setRating] = useState(0)
    const [comment, setComment] = useState("")
    const [saving, setSaving] = useState(false)
    const [retryToken, setRetryToken] = useState(0)

    const resetDraft = (from) => {
        setRating(from?.rating ?? 0)
        setComment(from?.comment ?? "")
    }

    useEffect(() => {
        let active = true
        setState("loading")
        api.get(`spark-instance/${sparkInstanceId}/rating`)
            .then(res => {
                if (!active) return
                const myRating = res.data.myRating
                setSaved(myRating)
                resetDraft(myRating)
                setEditing(!myRating) // not rated yet: open straight into the form
                setState("showing")
            })
            .catch(() => { if (active) setState("error") })
        return () => { active = false }
    }, [sparkInstanceId, retryToken])

    const startEdit = () => { resetDraft(saved); setSaveError(null); setEditing(true) }
    const cancelEdit = () => { resetDraft(saved); setSaveError(null); setEditing(false) }

    const save = () => {
        if (saving) return
        setSaving(true)
        setSaveError(null)
        api.put(`spark-instance/${sparkInstanceId}/rating`, { rating, comment: comment.trim() || null })
            .then(res => { setSaved(res.data); setEditing(false) })
            .catch(err => {
                const msg = err?.response?.data?.message
                setSaveError((Array.isArray(msg) ? msg.join(", ") : msg) ?? "Couldn't save your rating. Please try again.")
            })
            .finally(() => setSaving(false))
    }

    return (
        <div className="vi-spacer-s" style={{ cursor: "default" }} onClick={e => e.stopPropagation()}>
            {state === "loading" && <p className="card-label neutral">Loading your rating...</p>}

            {state === "error" && (
                <div className="flex center gap-xs">
                    <p className="card-label neutral">Couldn't load your rating.</p>
                    <button className="text" onClick={() => setRetryToken(t => t + 1)}>Try again</button>
                </div>
            )}

            {state === "showing" && !editing && saved && (
                <div className="flex space-between center gap-xs wrap">
                    <div className="flex columns gap-xxs">
                        <p className="card-label">Your rating</p>
                        <StarRating value={saved.rating} size={20} />
                        {saved.comment && <p className="card-body pre-wrap">{saved.comment}</p>}
                    </div>
                    <button className="text" onClick={startEdit}>Edit</button>
                </div>
            )}

            {state === "showing" && editing && (
                <div className="flex columns gap-xs">
                    <p className="card-body medium">How useful was this spark?</p>
                    <StarRating value={rating} onChange={v => { setRating(v); setSaveError(null) }} size={28} />
                    <div className="input-wrapper regular-border" style={{ height: "unset", alignItems: "unset" }}>
                        <ExpandingTextArea
                            placeholder="Anything you'd like to share? (optional)"
                            dynamicValue={comment}
                            onChange={v => { setComment(v.slice(0, MAX_COMMENT)); setSaveError(null) }}
                            minRows={2}
                            maxRows={5}
                        />
                    </div>
                    {saveError && <p className="card-label error">{saveError}</p>}
                    <div className="flex space-between center gap-xs wrap">
                        <p className="card-label neutral">
                            Shared anonymously with your program's organizers
                            {comment.length > MAX_COMMENT - 100 && ` · ${comment.length}/${MAX_COMMENT}`}
                        </p>
                        <div className="flex center gap-xs">
                            {saved && <button className="text" onClick={cancelEdit}>Cancel</button>}
                            <LoadingButton
                                className="solid"
                                label={saved ? "Save" : "Submit rating"}
                                loading={saving}
                                disabled={rating === 0 || saving}
                                onClick={save}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}